import Vue from 'vue';
import Vuex from 'vuex';
import bookmarkApi from '../api/bookmark-api';
import { isNullOrEmptyObject } from '@/utils/utils';

Vue.use(Vuex);

const initialState = () => {
  return {
    intranetList: [],
    noCategoryIntranetList: [],
    bookmarkList: [],
  };
};

const getters = {
  getIntranetList(state) {
    return state.intranetList;
  },
  getIsVisibleNoCategoryIntranetList(state) {
    return state.noCategoryIntranetList.filter((intranetInfo) => {
      return intranetInfo.bookmark_user_config.is_visible === 'Y';
    });
  },
  getBookmarkList(state) {
    return state.bookmarkList;
  },
};

const mutations = {
  setIntranetList(state, intranetList) {
    let isVisibleIntranetList = intranetList.filter((intranetInfo) => {
      return intranetInfo.bookmark_categories.length > 0;
    });
    // 1. 카테고리가 있는 북마크의 경우 카테고리 순서대로 정렬
    isVisibleIntranetList.sort((prev, next) => {
      return prev.order_no - next.order_no;
    });

    // 2. 카테고리가 없지만 조건에 충족하는 경우 새로운 카테고리를 만들어서 추가
    const noCategoryIntranetList = intranetList.filter((intranetInfo) => {
      return (
        intranetInfo.parent_category_no === null &&
        intranetInfo.bookmark_categories.length === 0 &&
        !isNullOrEmptyObject(intranetInfo.bookmark)
      );
    });

    isVisibleIntranetList.forEach((intranetInfo) => {
      intranetInfo.bookmark_categories.sort((prev, next) => {
        return prev.order_no - next.order_no;
      });
    });

    noCategoryIntranetList.sort((prev, next) => {
      return prev.order_no - next.order_no;
    });

    state.intranetList = isVisibleIntranetList;
    state.noCategoryIntranetList = noCategoryIntranetList;
  },
  setBookmarkList(state, bookmarkList) {
    bookmarkList.sort((prev, next) => {
      return prev.order_no - next.order_no;
    });
    state.bookmarkList = bookmarkList;
  },
  setIntranetVisible(state, { bookmarkCategoriesNo, isVisible }) {
    state.intranetList.forEach((intranetCategoryInfo, categoryIndex) => {
      let intranetIndex = intranetCategoryInfo.bookmark_categories.findIndex((intranetInfo) => {
        return intranetInfo.no === bookmarkCategoriesNo;
      });

      if (intranetIndex !== -1) {
        state.intranetList[categoryIndex]['bookmark_categories'][intranetIndex]['bookmark_user_config'][
          'is_visible'
        ] = isVisible ? 'Y' : 'N';
      }
    });
  },
  setNoCategoryIntranetVisible(state, { bookmarkCategoriesNo, isVisible }) {
    state.noCategoryIntranetList.forEach((intranetInfo, index) => {
      if (intranetInfo.no === bookmarkCategoriesNo) {
        state.noCategoryIntranetList[index]['bookmark_user_config']['is_visible'] = isVisible ? 'Y' : 'N';
      }
    });
  },

  setBookmarkVisible(state, { bookmarkCategoriesNo, isVisible }) {
    let bookmarkIndex = state.bookmarkList.findIndex((bookmarkInfo) => {
      return bookmarkInfo.no === bookmarkCategoriesNo;
    });

    state.bookmarkList[bookmarkIndex]['bookmark_user_config']['is_visible'] = isVisible ? 'Y' : 'N';
  },
  createBookmark(state, bookmarkInfo) {
    state.bookmarkList.push(bookmarkInfo);
  },
  modifyBookmark(state, { no, name, url }) {
    let bookmarkIndex = state.bookmarkList.findIndex((bookmarkInfo) => {
      return bookmarkInfo.no === no;
    });
    state.bookmarkList[bookmarkIndex]['bookmark']['url'] = url;
    state.bookmarkList[bookmarkIndex]['bookmark']['name'] = name;
  },
  removeBookmark(state, no) {
    let bookmarkIndex = state.bookmarkList.findIndex((bookmarkInfo) => {
      return bookmarkInfo.no === no;
    });

    state.bookmarkList.splice(bookmarkIndex, 1);
  },
  modifyBookmarkOrder(state, noOrderList) {
    let newBookmarkList = [];

    noOrderList.forEach((no, index) => {
      let bookmarkIndex = state.bookmarkList.findIndex((bookmarkInfo) => {
        return bookmarkInfo.no === parseInt(no);
      });
      state.bookmarkList[bookmarkIndex]['order_no'] = index;
      newBookmarkList.push(state.bookmarkList[bookmarkIndex]);
    });

    state.bookmarkList = newBookmarkList;
  },
};

const actions = {
  async loadIntranetList({ commit }) {
    return new Promise((resolve) => {
      commit('setIntranetList', []);
      bookmarkApi
        .loadIntranetList()
        .then((apiResponse) => {
          let data = apiResponse.data;

          // 백엔드 에러 대응을 위한 방어코드
          if (Array.isArray(data)) {
            commit('setIntranetList', data);
          } else {
            commit('setIntranetList', Object.values(data));
          }
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async loadBookmarkList({ commit }) {
    return new Promise((resolve) => {
      commit('setBookmarkList', []);
      bookmarkApi
        .loadBookmarkList()
        .then((apiResponse) => {
          let data = apiResponse.data;
          commit('setBookmarkList', data);
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async setIntranetVisible({ commit }, { bookmarkCategoriesNo, isVisible }) {
    commit('setIntranetVisible', { bookmarkCategoriesNo, isVisible });
    return bookmarkApi.setIntranetVisible(bookmarkCategoriesNo);
  },
  async setNoCategoryIntranetVisible({ commit }, { bookmarkCategoriesNo, isVisible }) {
    commit('setNoCategoryIntranetVisible', { bookmarkCategoriesNo, isVisible });
    return bookmarkApi.setIntranetVisible(bookmarkCategoriesNo);
  },
  async setBookmarkVisible({ commit }, { bookmarkCategoriesNo, isVisible }) {
    commit('setBookmarkVisible', { bookmarkCategoriesNo, isVisible });
    return bookmarkApi.setBookmarkVisible(bookmarkCategoriesNo);
  },
  async createBookmark({ commit }, { name, url }) {
    return new Promise((resolve) => {
      bookmarkApi
        .createBookmark(name, url)
        .then((apiResponse) => {
          let data = apiResponse.data;
          commit('createBookmark', data);
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async removeBookmark({ commit }, no) {
    return new Promise((resolve) => {
      bookmarkApi
        .removeBookmark(no)
        .then(() => {
          commit('removeBookmark', no);
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async modifyBookmark({ commit }, { no, name, url }) {
    return new Promise((resolve) => {
      bookmarkApi
        .modifyBookmark(no, name, url)
        .then(() => {
          commit('modifyBookmark', { no, name, url });
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async modifyBookmarkOrder({ commit }, noList) {
    return new Promise((resolve) => {
      bookmarkApi
        .modifyBookmarkOrder(noList)
        .then(() => {
          commit('modifyBookmarkOrder', noList);
        })
        .finally(() => {
          resolve();
        });
    });
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
  strict: process.env.NODE_ENV !== 'production',
};
